"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import { fetchJson } from "@/lib/api";
import {
  applyGlobalPreferences,
  syncGlobalPreferencesFromRecord,
  type GlobalPreferences,
} from "@/lib/global-preferences";

type Row = Record<string, unknown>;

// Preferences are per user, not per page: fetch them once per page load. The
// settings screens publish updates through the `tairoh-global-preferences`
// event, so re-fetching on every navigation only added latency.
let preferencesFetched = false;

export function GlobalPreferencesBridge() {
  const pathname = usePathname();

  useEffect(() => {
    void pathname;
    if (preferencesFetched) return;
    preferencesFetched = true;
    let alive = true;
    let notifyTimer: number | undefined;
    void fetchJson<Row>("/api/preferences/global", { cache: "no-store" })
      .then((data) => {
        if (!alive) return;
        syncGlobalPreferencesFromRecord(data as Record<string, unknown>);
        document.documentElement.lang = String(data.locale) === "en" ? "en" : "ja";
        // The bridge is mounted next to consumers that subscribe via
        // useSyncExternalStore. Defer the notification one task so their
        // subscriptions are installed before the initial preference fetch
        // can publish its result.
        notifyTimer = window.setTimeout(() => {
          if (!alive) return;
          window.dispatchEvent(
            new CustomEvent("tairoh-global-preferences", { detail: data }),
          );
        }, 0);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (notifyTimer !== undefined) window.clearTimeout(notifyTimer);
    };
    // Deliberately not keyed on pathname: preferences do not change per page.
  }, [pathname]);

  useEffect(() => {
    function onUpdate(event: Event) {
      const detail = (event as CustomEvent<Partial<GlobalPreferences>>).detail;
      if (detail) applyGlobalPreferences(detail);
    }
    window.addEventListener("tairoh-global-preferences", onUpdate as EventListener);
    return () => {
      window.removeEventListener("tairoh-global-preferences", onUpdate as EventListener);
    };
  }, []);

  return null;
}
